import { useEffect } from 'react';
import { FIRST_MEDIA_LOADED_ACTION } from '~/singlePageApp/screens/Project/hooks/usePollingMedia';
import { isNotUndefined } from '~/utilities/type-guards';
import { FIRST_COMMENT, FIRST_CUSTOMIZATION, FIRST_EDIT } from '../checklists/default';
import type { ChecklistItemsState } from '../OnboardingFooterContent';
import type { DefaultChecklistInfoQueryHookResult } from '../../gql/__gql/DefaultChecklistInfo.gql.gen';

export const UPLOAD_MEDIA_COMPLETE = 'UPLOAD_MEDIA_COMPLETE';
export const PLAYER_CUSTOMIZATION_UPDATED_ACTION = 'PLAYER_CUSTOMIZATION_UPDATED';
export const TEAM_COMMENT_POSTED = 'TEAM_COMMENT_POSTED';

type useRefetchOnMessageEventProps = ChecklistItemsState &
  Pick<DefaultChecklistInfoQueryHookResult, 'data' | 'refetch'>;
export const useRefetchOnMessageEvent = ({
  data,
  refetch,
  hasEditSession,
}: useRefetchOnMessageEventProps): void => {
  const hasNoMedia = isNotUndefined(data) && data.currentContact?.medias.nodes?.length === 0;
  const hasCompletedCustomizeChecklistItem =
    isNotUndefined(data) && FIRST_CUSTOMIZATION.isComplete(data);
  const hasCompletedEdit = isNotUndefined(data) && FIRST_EDIT.isComplete(data);
  const hasCompletedTeamComment = isNotUndefined(data) && FIRST_COMMENT.isComplete(data);

  useEffect(() => {
    // Since the checklist is not rendered on the Edit Page, we cannot listen for events that are fired
    if (!hasCompletedEdit && hasEditSession) {
      void refetch();
    }

    const listener = (event: MessageEvent<{ action: string }>) => {
      const {
        data: { action },
      } = event;

      if (
        // Refetch checklist data when user completes first media upload
        (hasNoMedia && action === UPLOAD_MEDIA_COMPLETE) ||
        // Refetch checklist data when user's first media has finished loading onto the page (AnonRecord Flow Only).
        action === FIRST_MEDIA_LOADED_ACTION ||
        // Refetch checklist data when user completes first customization
        (action === PLAYER_CUSTOMIZATION_UPDATED_ACTION && !hasCompletedCustomizeChecklistItem) ||
        // Refetch checklist data when user completes first team comment
        (action === TEAM_COMMENT_POSTED && !hasCompletedTeamComment)
      ) {
        void refetch();
      }
    };

    window.addEventListener('message', listener);

    return () => window.removeEventListener('message', listener);
  }, [
    data,
    refetch,
    hasCompletedCustomizeChecklistItem,
    hasCompletedEdit,
    hasEditSession,
    hasCompletedTeamComment,
    hasNoMedia,
  ]);
};
